// src/components/Toolbar/FileActions/SaveButton.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Save, Check, AlertCircle, Cloud } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModelStore } from '@stores/modelStore';
import { useGroupStore } from '@stores/groupStore';
import { useInitialDataManager } from '@hooks/useInitialDataManager';
import { iconPackManager } from '@/features/components/iconPacks';
import { useAuth } from '@/hooks/useAuth';
import { createDiagram, updateDiagram } from '@/services/diagramService';
import { toPng } from 'html-to-image';
import { useUiStateStore } from '@/stores/uiStateStore';
import { toast } from '@/stores/toastStore';
import type { Model } from '@/types/model';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';
type CloudSaveStatus = 'idle' | 'saving' | 'success' | 'error';

/**
 * Generate a thumbnail of the current diagram
 */
const generateThumbnail = async (): Promise<string | undefined> => {
  try {
    // Find the canvas element - look for the isometric canvas
    const canvasElement = document.querySelector(
      '[data-testid="isometric-canvas"]'
    ) as HTMLElement;
    if (!canvasElement) {
      console.warn('Canvas element not found for thumbnail generation');
      return undefined;
    }

    // Generate thumbnail at low quality (1x scale) and small size
    const dataUrl = await toPng(canvasElement, {
      cacheBust: true,
      pixelRatio: 0.5, // Low resolution for thumbnail
      width: canvasElement.offsetWidth,
      height: canvasElement.offsetHeight,
      backgroundColor: '#ffffff',
      skipFonts: true,
      // Filter to exclude UI elements
      filter: (node: HTMLElement) => {
        if (!node.classList) return true;

        const excludeClasses = [
          'MuiBackdrop',
          'MuiDialog',
          'transform-control',
          'selection-box',
          'resize-handle',
          'anchor-circle',
          'control-point',
          'connector-handle'
        ];

        for (const cls of excludeClasses) {
          if (node.classList.contains(cls)) {
            return false;
          }
        }

        if (node.getAttribute('data-element') === 'transform-control')
          return false;
        if (node.getAttribute('data-element') === 'cursor') return false;
        if (node.getAttribute('data-interaction-layer') === 'true')
          return false;

        return true;
      }
    });

    return dataUrl;
  } catch (error) {
    console.warn('Failed to generate thumbnail:', error);
    return undefined;
  }
};

export const SaveButton: React.FC = () => {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);

  // Cloud save state
  const [cloudStatus, setCloudStatus] = useState<CloudSaveStatus>('idle');
  const [currentDiagramId, setCurrentDiagramId] = useState<string | null>(null);
  const currentDiagramIdRef = React.useRef<string | null>(null);
  const cloudSaveInFlightRef = React.useRef(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Track the last saved model state to detect actual changes
  const lastSavedModelRef = React.useRef<string>('');

  const { load } = useInitialDataManager();

  // Get individual model properties (more stable than entire object)
  const version = useModelStore((state) => state.version);
  const title = useModelStore((state) => state.title);
  const description = useModelStore((state) => state.description);
  const colors = useModelStore((state) => state.colors);
  const icons = useModelStore((state) => state.icons);
  const items = useModelStore((state) => state.items);
  const views = useModelStore((state) => state.views);
  const currentViewId = useUiStateStore((state) => state.view);
  const groups = useGroupStore((state) => state.groups);
  const groupInstances = useGroupStore((state) => state.groupInstances);

  // Model items are reusable definitions and can exist without being placed.
  // Saving is enabled only when the active view contains drawable scene data.
  const currentView =
    views.find((view) => view.id === currentViewId) ?? views[0];
  const hasCanvasContent = Boolean(
    currentView &&
      ((currentView.items?.length ?? 0) > 0 ||
        (currentView.connectors?.length ?? 0) > 0 ||
        (currentView.rectangles?.length ?? 0) > 0 ||
        (currentView.textBoxes?.length ?? 0) > 0)
  );

  // Reconstruct model object when needed
  const getModel = useCallback(
    () => ({
      version,
      title,
      description,
      colors,
      icons,
      items,
      views,
      groups,
      groupInstances
    }),
    [
      version,
      title,
      description,
      colors,
      icons,
      items,
      views,
      groups,
      groupInstances
    ]
  );

  // Track changes to model - compare with last saved state
  useEffect(() => {
    // Create a comprehensive snapshot of the current state
    const currentModelString = JSON.stringify({
      items,
      views,
      title,
      description,
      colors,
      icons,
      groups,
      groupInstances
    });

    // On first render, just store the initial state
    if (!lastSavedModelRef.current) {
      lastSavedModelRef.current = currentModelString;
      return;
    }

    // Only mark as unsaved if the model actually changed from last save
    if (currentModelString !== lastSavedModelRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [items, views, title, description, colors, icons, groups, groupInstances]); // Watch ALL properties

  /**
   * Check if diagram has meaningful content
   */
  const hasContent = useCallback((model: Pick<Model, 'views'>) => {
    if (!Array.isArray(model.views)) return false;

    return model.views.some(
      (view) =>
        (view.items?.length ?? 0) > 0 ||
        (view.connectors?.length ?? 0) > 0 ||
        (view.rectangles?.length ?? 0) > 0 ||
        (view.textBoxes?.length ?? 0) > 0
    );
  }, []);

  /**
   * Save to localStorage (auto-save)
   */
  const saveToLocalStorage = useCallback(() => {
    try {
      const model = getModel();

      // Don't save empty diagrams
      if (!hasContent(model)) {
        discardAutoSave();
        return false;
      }

      // Gather all referenced icon IDs from items
      const referencedIconIds = new Set<string>();
      if (model.items) {
        model.items.forEach((item) => {
          if (item.icon) {
            referencedIconIds.add(item.icon);
          }
        });
      }

      // Ensure all referenced icons are in the icons array
      const existingIconIds = new Set(
        model.icons?.map((icon) => icon.id) || []
      );
      const missingIconIds = Array.from(referencedIconIds).filter(
        (id) => !existingIconIds.has(id)
      );

      // If there are missing icons, try to get them from icon packs
      let allIcons = [...(model.icons || [])];
      if (missingIconIds.length > 0) {
        missingIconIds.forEach((iconId) => {
          const iconDef = iconPackManager.getIconById(iconId);
          if (iconDef) {
            // Save in the model Icon format
            allIcons.push({
              id: iconDef.id,
              name: iconDef.name,
              url: iconDef.svgPath || '', // Use svgPath as url for model
              collection: iconDef.pack,
              isIsometric: iconDef.isIsometric
            });
          } else {
            console.warn(`  ✗ Icon not found: ${iconId}`);
          }
        });
      }

      // Validate and fix connector animation properties before saving
      const validatedViews = model.views?.map((view) => ({
        ...view,
        connectors: view.connectors?.map((connector) => ({
          ...connector,
          // Ensure animation properties meet minimum requirements
          flowSpeed:
            connector.flowSpeed !== undefined && connector.flowSpeed < 0.5
              ? 0.5
              : connector.flowSpeed,
          flowParticleCount:
            connector.flowParticleCount !== undefined &&
            connector.flowParticleCount < 1
              ? 1
              : connector.flowParticleCount,
          flowParticleSize:
            connector.flowParticleSize !== undefined &&
            connector.flowParticleSize < 4
              ? 4
              : connector.flowParticleSize
        }))
      }));

      // Create a clean copy of the model with all icons and validated connectors
      const cleanModel = {
        version: model.version || '1.0.0',
        title: model.title || 'Untitled',
        description: model.description || '',
        colors: model.colors || [],
        icons: allIcons,
        items: model.items || [],
        views:
          validatedViews && validatedViews.length > 0
            ? validatedViews
            : [
                {
                  id: 'default-view',
                  name: 'Main View',
                  items: [],
                  connectors: [],
                  rectangles: [],
                  textBoxes: []
                }
              ],
        groups: model.groups || [],
        groupInstances: model.groupInstances || []
      };

      const modelJSON = JSON.stringify(cleanModel);
      localStorage.setItem('flowi-autosave', modelJSON);
      localStorage.setItem(
        'flowi-autosave-timestamp',
        new Date().toISOString()
      );
      if (currentDiagramIdRef.current) {
        localStorage.setItem(
          'flowi-autosave-diagram-id',
          currentDiagramIdRef.current
        );
      } else {
        localStorage.removeItem('flowi-autosave-diagram-id');
      }
      setLastSaved(new Date());
      setHasUnsavedChanges(false);

      // Update the last saved state reference with complete model
      const currentModelString = JSON.stringify({
        items: model.items,
        views: validatedViews,
        title: model.title,
        description: model.description,
        colors: model.colors,
        icons: allIcons,
        groups: model.groups,
        groupInstances: model.groupInstances
      });
      lastSavedModelRef.current = currentModelString;

      return true;
    } catch (error) {
      console.error('Failed to auto-save to localStorage:', error);
      return false;
    }
  }, [getModel, hasContent]);

  /**
   * Extract diagram ID from URL on mount
   */
  useEffect(() => {
    const path = location.pathname;
    const match = path.match(/^\/diagram\/([^/]+)$/);
    if (match && match[1]) {
      setCurrentDiagramId(match[1]);
      currentDiagramIdRef.current = match[1];
    } else {
      setCurrentDiagramId(null);
      currentDiagramIdRef.current = null;
    }
  }, [location.pathname]);

  /**
   * Check for auto-save on mount and offer recovery
   * ONLY show restore prompt when:
   * 1. User is NOT logged in (logged in users use cloud storage)
   * 2. localStorage has autosave data
   * 3. Current diagram is NEW/empty (not reopening existing diagram)
   * 4. Autosave data has actual content
   */
  useEffect(() => {
    const checkAutoSave = () => {
      // Don't show restore prompt if user is logged in - they use cloud storage
      if (user) {
        console.log('User logged in - skipping localStorage restore check');
        return;
      }

      const autoSave = localStorage.getItem('flowi-autosave');
      const timestamp = localStorage.getItem('flowi-autosave-timestamp');
      const autoSaveDiagramId = localStorage.getItem(
        'flowi-autosave-diagram-id'
      );

      // Recovery data belongs to the diagram it was captured from. A newly
      // generated diagram ID must never inherit the previous diagram's draft.
      if (
        !currentDiagramIdRef.current ||
        autoSaveDiagramId !== currentDiagramIdRef.current
      ) {
        return;
      }

      if (autoSave && timestamp) {
        try {
          const savedDate = new Date(timestamp);
          const daysSinceAutoSave =
            (Date.now() - savedDate.getTime()) / (1000 * 60 * 60 * 24);

          // Only show recovery if < 7 days old
          if (daysSinceAutoSave < 7) {
            // Check if current model is empty/default (new diagram)
            const isEmpty = !items || items.length === 0;

            const savedModel = JSON.parse(autoSave) as Model;
            // Only show prompt if:
            // - Current diagram is empty (new diagram)
            // - Saved model has content
            if (isEmpty && hasContent(savedModel)) {
              setShowRecoveryPrompt(true);
              setLastSaved(savedDate);
              console.log('📦 Auto-save recovery available for new diagram');
            }
          }
        } catch (error) {
          console.error('Failed to check auto-save:', error);
        }
      }
    };

    // Check after a short delay to let the app initialize
    setTimeout(checkAutoSave, 1000);
  }, [hasContent, items, user]);

  /**
   * Recover from auto-save
   */
  const recoverAutoSave = () => {
    const autoSave = localStorage.getItem('flowi-autosave');
    if (autoSave) {
      try {
        const savedModel = JSON.parse(autoSave) as Model;

        // Validate the model has required fields
        if (!savedModel.version) savedModel.version = '1.0.0';
        if (!savedModel.title) savedModel.title = 'Untitled';
        if (!savedModel.description) savedModel.description = '';
        if (!savedModel.colors) savedModel.colors = [];
        if (!savedModel.icons) savedModel.icons = [];
        if (!savedModel.items) savedModel.items = [];
        if (!savedModel.views || savedModel.views.length === 0) {
          savedModel.views = [
            {
              id: 'default-view',
              name: 'Main View',
              items: [],
              connectors: [],
              rectangles: [],
              textBoxes: []
            }
          ];
        }

        // Fix connector animation properties to meet validation requirements
        if (savedModel.views) {
          savedModel.views.forEach((view) => {
            if (view.connectors) {
              view.connectors.forEach((connector) => {
                // Ensure flowSpeed is >= 0.5
                if (
                  connector.flowSpeed !== undefined &&
                  connector.flowSpeed < 0.5
                ) {
                  connector.flowSpeed = 0.5;
                }
                // Ensure flowParticleCount is >= 1
                if (
                  connector.flowParticleCount !== undefined &&
                  connector.flowParticleCount < 1
                ) {
                  connector.flowParticleCount = 1;
                }
                // Ensure flowParticleSize is >= 4
                if (
                  connector.flowParticleSize !== undefined &&
                  connector.flowParticleSize < 4
                ) {
                  connector.flowParticleSize = 4;
                }
              });
            }
          });
        }

        // Load the validated model
        load(savedModel);
        setShowRecoveryPrompt(false);
      } catch (error) {
        console.error('Failed to recover auto-save:', error);
        toast.error(
          `Failed to recover auto-save: ${error instanceof Error ? error.message : 'Unknown error'}\n\nThe auto-save data may be corrupted. Starting fresh instead.`
        );
        discardAutoSave();
      }
    }
  };

  /**
   * Discard auto-save
   */
  const discardAutoSave = () => {
    localStorage.removeItem('flowi-autosave');
    localStorage.removeItem('flowi-autosave-timestamp');
    localStorage.removeItem('flowi-autosave-diagram-id');
    setShowRecoveryPrompt(false);
  };

  /**
   * Download as JSON file (manual save)
   */
  const downloadAsJSON = useCallback(async () => {
    setStatus('saving');

    try {
      const model = getModel();

      // Use the same icon gathering logic as auto-save
      const referencedIconIds = new Set<string>();
      if (model.items) {
        model.items.forEach((item) => {
          if (item.icon) {
            referencedIconIds.add(item.icon);
          }
        });
      }

      // Ensure all referenced icons are in the icons array
      const existingIconIds = new Set(
        model.icons?.map((icon) => icon.id) || []
      );
      const missingIconIds = Array.from(referencedIconIds).filter(
        (id) => !existingIconIds.has(id)
      );

      // If there are missing icons, try to get them from icon packs
      let allIcons = [...(model.icons || [])];
      if (missingIconIds.length > 0) {
        missingIconIds.forEach((iconId) => {
          const iconDef = iconPackManager.getIconById(iconId);
          if (iconDef) {
            // Save in the model Icon format
            allIcons.push({
              id: iconDef.id,
              name: iconDef.name,
              url: iconDef.svgPath || '', // Use svgPath as url for model
              collection: iconDef.pack,
              isIsometric: iconDef.isIsometric
            });
          } else {
            console.warn(`  ✗ Icon not found: ${iconId}`);
          }
        });
      }

      // Create model with all icons
      // const exportModel = {
      //   ...model,
      //   icons: allIcons
      // };

      // Create JSON string
      // const jsonString = JSON.stringify(exportModel, null, 2);
      // const filename = `${model.title || 'flowi-diagram'}.json`;

      // // Create blob and download
      // const blob = new Blob([jsonString], { type: 'application/json' });
      // const url = URL.createObjectURL(blob);
      // const link = document.createElement('a');
      // link.href = url;
      // link.download = filename;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // URL.revokeObjectURL(url);

      // Also save to localStorage
      saveToLocalStorage();

      setStatus('success');
      toast.success('Diagram saved locally.');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (error) {
      console.error('Save failed:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  }, [getModel, saveToLocalStorage]);

  /**
   * Save to cloud (Supabase)
   */
  const saveToCloud = useCallback(async () => {
    if (!user) {
      toast.warning('Please log in to save to the cloud.');
      return;
    }

    // Button clicks, Ctrl+S, and autosave can fire in the same render cycle.
    // React state is not a synchronous mutex, so guard the request with a ref.
    if (cloudSaveInFlightRef.current) return;
    cloudSaveInFlightRef.current = true;

    setCloudStatus('saving');

    try {
      const model = getModel();

      // Generate thumbnail
      const thumbnail = await generateThumbnail();

      // Get all icons with same logic as local save
      const referencedIconIds = new Set<string>();
      if (model.items) {
        model.items.forEach((item) => {
          if (item.icon) {
            referencedIconIds.add(item.icon);
          }
        });
      }

      const existingIconIds = new Set(
        model.icons?.map((icon) => icon.id) || []
      );
      const missingIconIds = Array.from(referencedIconIds).filter(
        (id) => !existingIconIds.has(id)
      );

      let allIcons = [...(model.icons || [])];
      if (missingIconIds.length > 0) {
        missingIconIds.forEach((iconId) => {
          const iconDef = iconPackManager.getIconById(iconId);
          if (iconDef) {
            allIcons.push({
              id: iconDef.id,
              name: iconDef.name,
              url: iconDef.svgPath || '',
              collection: iconDef.pack,
              isIsometric: iconDef.isIsometric
            });
          }
        });
      }

      const diagramData = {
        version: model.version || '1.0.0',
        title: model.title || 'Untitled',
        description: model.description || '',
        colors: model.colors || [],
        icons: allIcons,
        items: model.items || [],
        views: model.views || [],
        groups: model.groups || [],
        groupInstances: model.groupInstances || []
      };

      let result;
      const diagramId = currentDiagramIdRef.current;
      if (diagramId) {
        // NewDiagramRedirect reserves the URL UUID before a database row
        // exists, so the first save starts as an update attempt.
        result = await updateDiagram(
          diagramId,
          diagramData,
          model.title,
          model.description,
          thumbnail
        );
        if (result.error?.message.includes('PGRST116')) {
          // Create the initially reserved row with the SAME UUID. Using a new
          // generated ID here is what previously produced duplicate cards.
          result = await createDiagram(
            diagramData,
            model.title,
            model.description,
            thumbnail,
            diagramId
          );
        } else if (result.error) {
          console.error(
            '❌ Diagram update failed:',
            result.error
          );
        } else {
          console.log('✅ Diagram updated successfully');
        }
      } else {
        // Create new diagram without specific ID
        result = await createDiagram(
          diagramData,
          model.title,
          model.description,
          thumbnail
        );

        if (result.data) {
          const newId = result.data.id;
          // Update the ref synchronously before navigation or another save can
          // observe this diagram as new.
          currentDiagramIdRef.current = newId;
          setCurrentDiagramId(newId);
          // Navigate to the diagram's unique URL
          navigate(`/diagram/${newId}`, { replace: true });
        }
      }

      if (result.error) {
        throw result.error;
      }

      lastSavedModelRef.current = JSON.stringify({
        items: model.items,
        views: model.views,
        title: model.title,
        description: model.description,
        colors: model.colors,
        icons: allIcons,
        groups: model.groups,
        groupInstances: model.groupInstances
      });
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      setCloudStatus('success');
      setTimeout(() => setCloudStatus('idle'), 2000);
    } catch (error) {
      console.error('Cloud save failed:', error);
      setCloudStatus('error');
      setTimeout(() => setCloudStatus('idle'), 3000);
      toast.error(
        `Failed to save to cloud: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      cloudSaveInFlightRef.current = false;
    }
  }, [user, getModel, navigate]);

  /**
   * Unified save function - saves to cloud if authenticated, otherwise local
   */
  const handleSave = useCallback(async () => {
    if (!hasCanvasContent) return;

    if (user) {
      // User is logged in - save to cloud
      await saveToCloud();
    } else {
      // Not logged in - save to local storage
      downloadAsJSON();
    }
  }, [hasCanvasContent, user, saveToCloud, downloadAsJSON]);

  /**
   * Auto-save every 30 seconds
   * - If logged in: save to cloud
   * - If not logged in: save to localStorage
   */
  useEffect(() => {
    const interval = setInterval(() => {
      if (hasCanvasContent && hasUnsavedChanges) {
        if (user) {
          // Logged in: auto-save to cloud
          saveToCloud();
        } else {
          // Not logged in: auto-save to localStorage
          saveToLocalStorage();
        }
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [
    hasCanvasContent,
    hasUnsavedChanges,
    saveToLocalStorage,
    saveToCloud,
    user
  ]);

  /**
   * Save on Ctrl+S / Cmd+S
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const getButtonContent = () => {
    const isSaving = user ? cloudStatus === 'saving' : status === 'saving';
    const isSuccess = user ? cloudStatus === 'success' : status === 'success';
    const isError = user ? cloudStatus === 'error' : status === 'error';

    if (isSaving) {
      return (
        <>
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <span className="hidden md:inline">Saving...</span>
        </>
      );
    }

    if (isSuccess) {
      return (
        <>
          <Check className="h-4 w-4" />
          <span className="hidden md:inline">Saved!</span>
        </>
      );
    }

    if (isError) {
      return (
        <>
          <AlertCircle className="h-4 w-4" />
          <span className="hidden md:inline">Failed</span>
        </>
      );
    }

    return (
      <>
        {user ? <Cloud className="h-4 w-4" /> : <Save className="h-4 w-4" />}
        <span className="hidden md:inline">Save</span>
        {hasUnsavedChanges && (
          <span
            className="ml-1 h-2 w-2 rounded-full bg-orange-500"
            title="Unsaved changes"
          />
        )}
      </>
    );
  };

  const getTooltip = () => {
    if (user) {
      return currentDiagramId
        ? `Save changes (Ctrl+S)`
        : `Save diagram (Ctrl+S)`;
    }

    if (lastSaved) {
      const timeAgo = Math.floor((Date.now() - lastSaved.getTime()) / 1000);
      if (timeAgo < 60) return `Auto-saved ${timeAgo}s ago (Ctrl+S)`;
      if (timeAgo < 3600)
        return `Auto-saved ${Math.floor(timeAgo / 60)}m ago (Ctrl+S)`;
      return `Auto-saved ${Math.floor(timeAgo / 3600)}h ago (Ctrl+S)`;
    }
    return 'Save diagram (Ctrl+S)';
  };

  return (
    <>
      {/* Unified Save Button - Cloud if logged in, Local if not */}
      <button
        onClick={handleSave}
        disabled={
          !hasCanvasContent ||
          (user ? cloudStatus === 'saving' : status === 'saving')
        }
        className={`group relative flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
          user
            ? 'text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300'
            : 'text-gray-700 hover:bg-white hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
        }`}
        title={!hasCanvasContent ? 'Cannot save empty canvas' : getTooltip()}
      >
        {getButtonContent()}
      </button>

      {/* Recovery Prompt - Inline Toast */}
      {showRecoveryPrompt && (
        <div className="fixed bottom-6 right-6 z-50 w-96 rounded-xl border-2 border-blue-500 bg-white p-4 shadow-2xl dark:bg-gray-800">
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
              <Cloud className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Recover Previous Work?
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                We found an auto-saved version from{' '}
                {lastSaved &&
                Math.floor((Date.now() - lastSaved.getTime()) / 60000) < 60
                  ? `${Math.floor((Date.now() - lastSaved.getTime()) / 60000)} minutes ago`
                  : lastSaved?.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={discardAutoSave}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Start Fresh
            </button>
            <button
              onClick={recoverAutoSave}
              className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Recover
            </button>
          </div>
        </div>
      )}
    </>
  );
};
